import { type ResultOf } from '@graphql-typed-document-node/core'
import { type Atom, type PrimitiveAtom, type WritableAtom } from 'jotai'
import { type atomWithReset } from 'jotai/utils'

import { graphql } from '~/gql'
import {
  type DataDisplayType,
  type Group,
  type ResultViewerType,
  type SortValue,
} from '~/utils'

import {
  type DisplayColumnKey,
  type DisplayRowId,
  type GroupsDiff,
  type QuestionsDiffsMap,
} from './common'
import { type IndividualSortColumnKey } from './individual'
import { type MatrixSortColumnKey } from './matrix'
import { type SequentialSortColumnKey } from './sequential'

const questionTabulationQueryDataFragment = graphql(`
  fragment GroupCountsFigures_QuestionTabulationQueryData on CountAnswersByMultiQuestionsResult {
    questions
    groupCounts
    results {
      answers {
        answerOption {
          ...GroupCountsFigures_AnswerOption
        }
        count
      }
      groupAnswers {
        answers {
          answerOption {
            ...GroupCountsFigures_AnswerOption
          }
          count
        }
        group
      }
      questionId
    }
  }
`)

const countCustomerAttributeTabulationQueryDataFragment = graphql(`
  fragment GroupCountsFigures_CustomerAttributeTabulationQueryData on CountCustomerAttributeGroupsResult {
    customerAttributeId
    result {
      totalCount
      groupResults {
        group
        count
      }
    }
  }
`)

type AtomWithReset<Value> = ReturnType<typeof atomWithReset<Value>>

export type CommentAtom = AtomWithReset<Record<string, string>>
export type DataDisplayTypeAtom = WritableAtom<
  DataDisplayType,
  [DataDisplayType],
  void
>
export type ResultViewerTypeAtom = AtomWithReset<ResultViewerType>
export type GroupNameMapAtom = AtomWithReset<Record<Group, string>>

export type SequentialSortValueAtom =
  AtomWithReset<SortValue<SequentialSortColumnKey> | null>
export type IndividualSortValueAtom =
  AtomWithReset<SortValue<IndividualSortColumnKey> | null>
export type MatrixSortValueAtom =
  AtomWithReset<SortValue<MatrixSortColumnKey> | null>

export type HiddenRowIdsAtom = AtomWithReset<DisplayRowId[]>
export type HiddenColumnKeysAtom = AtomWithReset<DisplayColumnKey[]>

export type GroupsDiffsAtom = PrimitiveAtom<GroupsDiff[]>
export type QuestionsDiffsMapAtom = PrimitiveAtom<QuestionsDiffsMap>

export type QuestionTabulationQueryData = ResultOf<
  typeof questionTabulationQueryDataFragment
>
export type CountAnswers = NonNullable<
  NonNullable<QuestionTabulationQueryData['results']>[number]
>[number]

export type QuestionQueryDataAtom = Atom<QuestionTabulationQueryData | null>
export type CustomerAttributeQueryDataAtom = Atom<
  ResultOf<typeof countCustomerAttributeTabulationQueryDataFragment>[] | null
>

export type IsTabulatedAtom = Atom<boolean>
export type NameEditingGroupAtom = PrimitiveAtom<Group | null>
